import {
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";

// Fixed page chrome the tooltip must never be drawn over (it is clipped where they overlap).
const HEADER_SELECTOR: string = ".Header_header";
const PRICE_BAR_SELECTOR: string = ".total-price-mobile";
const ARROW_ALLOWANCE_PX: number = 8;

@Directive({
  selector: "[appTooltip]",
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  @Input("appTooltip") public text: string | TemplateRef<unknown> = "";

  @Output() public appTooltipMouseenter = new EventEmitter<MouseEvent>();
  @Output() public appTooltipMouseleave = new EventEmitter<MouseEvent>();

  private _tooltipEl: HTMLElement | null = null;
  private _embeddedView: EmbeddedViewRef<unknown> | null = null;
  private _removeScrollListeners: (() => void) | null = null;
  // a tap fires touchstart, then an emulated mouseenter, then click. No mouseleave follows, so
  // touch needs its own way to close: tapping the host again, or tapping anywhere else.
  // above or below the icon, decided when the tooltip opens and kept while the page scrolls
  private _placedBelow: boolean | null = null;
  private _touchStartedWhileShown: boolean = false;
  private _lastTouchAt: number = 0;

  constructor(
    private _el: ElementRef<HTMLElement>,
    private _renderer: Renderer2,
    private _vcr: ViewContainerRef,
  ) {}

  @HostListener("touchstart")
  public onTouchStart(): void {
    this._touchStartedWhileShown = this._tooltipEl !== null;
    this._lastTouchAt = Date.now();
  }

  @HostListener("click")
  public onClick(): void {
    if (this._isTouchInteraction() && this._touchStartedWhileShown) {
      this._hide();
    }
    this._touchStartedWhileShown = false;
  }

  @HostListener("mouseenter", ["$event"])
  public onMouseEnter(event: MouseEvent): void {
    // the emulated mouseenter of a tap on an already open tooltip must not reopen it
    if (!(this._isTouchInteraction() && this._touchStartedWhileShown)) {
      this._show();
    }
    this.appTooltipMouseenter.emit(event);
  }

  @HostListener("mouseleave", ["$event"])
  public onMouseLeave(event: MouseEvent): void {
    this._hide();
    this.appTooltipMouseleave.emit(event);
  }

  public ngOnDestroy(): void {
    this._hide();
  }

  private _show(): void {
    if (!this.text || this._tooltipEl) return;

    this._tooltipEl = this._renderer.createElement("div");
    this._renderer.addClass(this._tooltipEl, "c-tooltip");

    if (this.text instanceof TemplateRef) {
      this._embeddedView = this._vcr.createEmbeddedView(this.text);
      this._embeddedView.detectChanges();
      this._embeddedView.rootNodes.forEach((node) => {
        this._renderer.appendChild(this._tooltipEl, node);
      });
    } else {
      this._renderer.setProperty(this._tooltipEl, "textContent", this.text);
    }

    this._renderer.appendChild(document.body, this._tooltipEl);
    this._position();
    this._listenForScroll();
  }

  private _hide(): void {
    this._placedBelow = null;
    this._removeScrollListeners?.();
    this._removeScrollListeners = null;

    if (this._tooltipEl) {
      this._renderer.removeChild(document.body, this._tooltipEl);
      this._tooltipEl = null;
    }

    if (this._embeddedView) {
      this._embeddedView.destroy();
      this._embeddedView = null;
    }
  }

  // the tooltip is position: fixed, so it has to follow its host while any ancestor scrolls
  private _listenForScroll(): void {
    const reposition: () => void = (): void => this._position();
    window.addEventListener("scroll", reposition, { capture: true, passive: true });
    window.addEventListener("resize", reposition, { passive: true });
    const hideOnOutsideTouch: (event: TouchEvent) => void = (event: TouchEvent): void => {
      if (!this._el.nativeElement.contains(event.target as Node)) {
        this._hide();
      }
    };
    document.addEventListener("touchstart", hideOnOutsideTouch, { passive: true });
    this._removeScrollListeners = (): void => {
      document.removeEventListener("touchstart", hideOnOutsideTouch);
      window.removeEventListener("scroll", reposition, { capture: true });
      window.removeEventListener("resize", reposition);
    };
  }

  private _isTouchInteraction(): boolean {
    return Date.now() - this._lastTouchAt < 1000;
  }

  // Viewport band the tooltip may occupy: below the fixed header, above the price bar. The bar
  // only limits tooltips whose icon is not inside it.
  private _visibleBounds(): { top: number; bottom: number } {
    let top: number = 0;
    let bottom: number = window.innerHeight;

    const header: Element | null = document.querySelector(HEADER_SELECTOR);
    if (header && getComputedStyle(header).position === "fixed") {
      top = Math.max(top, header.getBoundingClientRect().bottom);
    }

    const priceBar: Element | null = document.querySelector(PRICE_BAR_SELECTOR);
    if (
      priceBar &&
      getComputedStyle(priceBar).display !== "none" &&
      !priceBar.contains(this._el.nativeElement)
    ) {
      bottom = Math.min(bottom, priceBar.getBoundingClientRect().top);
    }

    return { top, bottom };
  }

  private _position(): void {
    if (!this._tooltipEl) return;

    const host: DOMRect = this._el.nativeElement.getBoundingClientRect();
    const tip: DOMRect = this._tooltipEl.getBoundingClientRect();
    const bounds: { top: number; bottom: number } = this._visibleBounds();

    const above: number = host.top - tip.height - 8;
    // the side is chosen once, so the tooltip does not jump below the icon while scrolling
    this._placedBelow ??= above < bounds.top;
    const top: number = this._placedBelow ? host.bottom + 8 : above;
    let left: number = host.left + host.width / 2 - tip.width / 2;

    left = Math.max(8, Math.min(left, window.innerWidth - tip.width - 8));

    if (this._placedBelow) {
      this._renderer.addClass(this._tooltipEl, "c-tooltip--below");
    } else {
      this._renderer.removeClass(this._tooltipEl, "c-tooltip--below");
    }
    this._renderer.setStyle(this._tooltipEl, "top", `${top}px`);
    this._renderer.setStyle(this._tooltipEl, "left", `${left}px`);

    const iconCenterX: number = host.left + host.width / 2;
    const arrowLeft: number = Math.max(12, Math.min(iconCenterX - left, tip.width - 12));
    this._tooltipEl.style.setProperty("--tooltip-arrow-left", `${arrowLeft}px`);

    this._clipToBounds(top, tip.height, bounds);
  }

  private _clipToBounds(
    top: number,
    height: number,
    bounds: { top: number; bottom: number },
  ): void {
    if (!this._tooltipEl) return;

    const cutTop: number = Math.min(height, Math.max(0, bounds.top - top));
    const cutBottom: number = Math.min(height, Math.max(0, top + height - bounds.bottom));

    if (cutTop === 0 && cutBottom === 0) {
      this._renderer.removeStyle(this._tooltipEl, "clip-path");
      return;
    }

    // a negative inset leaves room for the arrow, which sits outside the box
    const insetTop: number = cutTop === 0 ? -ARROW_ALLOWANCE_PX : cutTop;
    const insetBottom: number = cutBottom === 0 ? -ARROW_ALLOWANCE_PX : cutBottom;
    this._renderer.setStyle(
      this._tooltipEl,
      "clip-path",
      `inset(${insetTop}px -${ARROW_ALLOWANCE_PX}px ${insetBottom}px -${ARROW_ALLOWANCE_PX}px)`,
    );
  }
}
